import {
  Image as ImageIcon,
  Description as DescriptionIcon,
  CalendarMonth as CalendarIcon,
  ShoppingCart as ShoppingCartIcon,
  People as PeopleIcon,
  LocationOn as LocationOnIcon,
  Work as WorkIcon,
  RateReview as RateReviewIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Share as ShareIcon,
  Link as LinkIcon,
  Phone as PhoneIcon,
  Help as HelpIcon,
} from '@mui/icons-material';

import { PageType, BlockType, SelectorKind, PriorityEntity } from '../../../catalogs/pageCatalog.generated';

import HeroBlockEditor from './HeroBlockEditor';
import AboutBlockEditor from './AboutBlockEditor';
import SchedulesBlockEditor from './SchedulesBlockEditor';
import ServicesBlockEditor from './ServicesBlockEditor';
import TeamBlockEditor from './TeamBlockEditor';
import LocationsBlockEditor from './LocationsBlockEditor';
import ExperienceBlockEditor from './ExperienceBlockEditor';
import TestimonialsBlockEditor from './TestimonialsBlockEditor';
import GalleryBlockEditor from './GalleryBlockEditor';
import SocialLinksBlockEditor from './SocialLinksBlockEditor';
import GenericLinksBlockEditor from './GenericLinksBlockEditor';
import ContactBlockEditor from './ContactBlockEditor';
import FaqBlockEditor from './FaqBlockEditor';

/**
 * blockRegistry — Registro central de tipos de blocos
 *
 * TODO 4.18: Registry completo — todos os 13 editores importados e registrados.
 * Cada entrada define:
 *   - label: nome amigável (pt-BR)
 *   - icon: componente ícone MUI (referência, não renderizado aqui)
 *   - description: parágrafo explicativo ≥1 linha (O11)
 *   - defaultConfig: valores iniciais garantindo leitura tolerante (O7)
 *   - editorComponent: componente editor real (TODOs 4.5–4.17)
 *   - allowedIn: array de siteTypes suportados (matriz §7.14)
 *
 * Obrigações:
 * - O11: description com ≥1 parágrafo
 * - O10: em src/components/pages/blocks/
 * - O7: defaultConfig com defaults explícitos
 */

// Definições por nome (camelCase = nome do BlockType). O registry exportado é
// derivado abaixo, chaveado por int (BlockType) — modelo v3, sem string.
const BLOCK_DEFS = {
  hero: {
    label: 'Capa (Hero)',
    icon: ImageIcon,
    description:
      'Primeiro bloco visível da página. Apresenta a identidade com título grande, subtítulo, imagem principal e formato de avatar. Funciona como cartão de visita: reduz fricção para o visitante entender instantaneamente de quem/o que se trata a página.',
    defaultConfig: {
      title: '',
      subtitle: '',
      // imageUrl e avatarShape vivem em identity (D4 do refactor plan).
    },
    editorComponent: HeroBlockEditor,
    allowedIn: [PageType.LinkInBio, PageType.Professional, PageType.Company],
  },

  about: {
    label: 'Sobre',
    icon: DescriptionIcon,
    description:
      'Bloco de texto rico para contar história, formação, filosofia de atendimento ou missão do estabelecimento. É o bloco mais humanizador do site. Editor WYSIWYG com suporte a formatação básica e limite de 5000 caracteres.',
    defaultConfig: {
      html: '',
    },
    editorComponent: AboutBlockEditor,
    allowedIn: [PageType.LinkInBio, PageType.Professional, PageType.Company],
  },

  schedules: {
    label: 'Agendas / Booking',
    icon: CalendarIcon,
    description:
      'O coração funcional da página. Permite que o visitante escolha e agende. Consolida múltiplas agendas com seletores configuráveis e ordem de escolha personalizável. Suporta modo "Qualquer Profissional" que agrupa horários e sorteia quem atenderá.',
    defaultConfig: {
      scheduleIds: [],
      selectorKind: SelectorKind.Dropdown,
      priorityOrder: [PriorityEntity.Service, PriorityEntity.Professional, PriorityEntity.Place],
      anyProfessional: false,
      ctaLabel: 'Agendar',
      showServiceDescription: false,
    },
    editorComponent: SchedulesBlockEditor,
    allowedIn: [PageType.LinkInBio, PageType.Professional, PageType.Company],
  },

  services: {
    label: 'Serviços',
    icon: ShoppingCartIcon,
    description:
      'Lista de serviços oferecidos com nome, descrição, duração e preço. Quando aparece junto do bloco de agendas, ajuda o visitante a conhecer opções antes de agendar. Suporta importação de serviços cadastrados.',
    defaultConfig: {
      items: [],
    },
    editorComponent: ServicesBlockEditor,
    allowedIn: [PageType.Professional, PageType.Company],
  },

  team: {
    label: 'Equipe',
    icon: PeopleIcon,
    description:
      'Grade de profissionais do estabelecimento com foto, nome e função. Ajuda o visitante a identificar quem compõe a equipe antes de agendar. Suporta apenas em páginas de empresa/local.',
    defaultConfig: {
      members: [],
    },
    editorComponent: TeamBlockEditor,
    allowedIn: [PageType.Company],
  },

  locations: {
    label: 'Locais',
    icon: LocationOnIcon,
    description:
      'Cartões de endereço de todos os locais envolvidos na página. Traz nome, endereço completo, telefone e mapa opcional. Essencial para páginas multilocais. Suporta apenas em páginas de empresa/local.',
    defaultConfig: {
      items: [],
    },
    editorComponent: LocationsBlockEditor,
    allowedIn: [PageType.Company],
  },

  experience: {
    label: 'Formação e Experiência',
    icon: WorkIcon,
    description:
      'Linha do tempo com formação, especializações, cursos e experiência profissional. Construído manualmente com períodos, instituições e descrições. Suporta apenas em páginas profissionais.',
    defaultConfig: {
      items: [],
    },
    editorComponent: ExperienceBlockEditor,
    allowedIn: [PageType.Professional],
  },

  testimonials: {
    label: 'Depoimentos',
    icon: RateReviewIcon,
    description:
      'Depoimentos de clientes e pacientes. Humaniza e convence visitantes. Construído manualmente com nome, texto, avaliação em estrelas (1–5) e data. Suporta todos os tipos de página.',
    defaultConfig: {
      items: [],
    },
    editorComponent: TestimonialsBlockEditor,
    allowedIn: [PageType.LinkInBio, PageType.Professional, PageType.Company],
  },

  gallery: {
    label: 'Galeria',
    icon: PhotoLibraryIcon,
    description:
      'Grade de imagens (ambiente, antes/depois, equipe). Peso visual forte — use poucas imagens de boa qualidade. Recomendado até 10 imagens com resolução mínima 1024px no lado maior.',
    defaultConfig: {
      images: [],
    },
    editorComponent: GalleryBlockEditor,
    allowedIn: [PageType.Professional, PageType.Company],
  },

  socialLinks: {
    label: 'Redes Sociais',
    icon: ShareIcon,
    description:
      'Ícones clicáveis para redes sociais (Instagram, Facebook, LinkedIn, YouTube, Twitter, TikTok, Threads). Compacto e universal. Suporta todos os tipos de página.',
    defaultConfig: {
      instagram: '',
      facebook: '',
      linkedin: '',
      youtube: '',
      twitter: '',
      tiktok: '',
      threads: '',
    },
    editorComponent: SocialLinksBlockEditor,
    allowedIn: [PageType.LinkInBio, PageType.Professional, PageType.Company],
  },

  genericLinks: {
    label: 'Links Gerais',
    icon: LinkIcon,
    description:
      'Lista de links extras com rótulo personalizado. Útil para apontar para blog, portfólio externo, WhatsApp ou outros destinos. Suporta todos os tipos de página.',
    defaultConfig: {
      items: [],
    },
    editorComponent: GenericLinksBlockEditor,
    allowedIn: [PageType.LinkInBio, PageType.Professional, PageType.Company],
  },

  contact: {
    label: 'Contato',
    icon: PhoneIcon,
    description:
      'Cartão com telefone, WhatsApp, email e site. Existe como bloco explícito para templates que querem seção dedicada. Em páginas profissionais, valores-padrão vêm do cadastro de profissional; pode sobrescrever aqui.',
    defaultConfig: {
      phone: '',
      whatsapp: '',
      email: '',
      website: '',
    },
    editorComponent: ContactBlockEditor,
    allowedIn: [PageType.LinkInBio, PageType.Professional, PageType.Company],
  },

  faq: {
    label: 'Perguntas Frequentes',
    icon: HelpIcon,
    description:
      'Pares pergunta/resposta em accordion. Reduz dúvidas operacionais (horário, convênios) e melhora SEO. Cada item tem pergunta (até 150 caracteres) e resposta (até 500 caracteres).',
    defaultConfig: {
      items: [],
    },
    editorComponent: FaqBlockEditor,
    allowedIn: [PageType.Professional, PageType.Company],
  },
};

// Registry canônico: chaveado por BlockType (int). Cada entrada carrega seu `type`
// (int) para o menu de adicionar. Lookup: BLOCK_REGISTRY[block.type] (block.type é int).
const capFirst = (s) => s.charAt(0).toUpperCase() + s.slice(1);
export const BLOCK_REGISTRY = Object.fromEntries(
  Object.entries(BLOCK_DEFS).map(([name, def]) => {
    const type = BlockType[capFirst(name)];
    return [type, { type, ...def }];
  })
);

export default BLOCK_REGISTRY;
